/*
 * Copyright (c) 2020 - 2025 Ping Identity Corporation. All rights reserved.
 *
 * This software may be modified and distributed under the terms
 * of the MIT license. See the LICENSE file for details.
 */
import { StepType } from '@forgerock/sdk-types';
import { createCallback } from './callbacks/factory.js';
function getCallbacksOfType(callbacks, type) {
    return callbacks.filter((x) => x.getType() === type);
}
function getCallbackOfType(callbacks, type) {
    const callbacksOfType = getCallbacksOfType(callbacks, type);
    if (callbacksOfType.length !== 1) {
        throw new Error(`Expected 1 callback of type "${type}", but found ${callbacksOfType.length}`);
    }
    return callbacksOfType[0];
}
function setCallbackValue(callbacks, type, value) {
    const callbacksToUpdate = getCallbacksOfType(callbacks, type);
    if (callbacksToUpdate.length !== 1) {
        throw new Error(`Expected 1 callback of type "${type}", but found ${callbacksToUpdate.length}`);
    }
    callbacksToUpdate[0].setInputValue(value);
}
function getDescription(payload) {
    return payload.description;
}
function getHeader(payload) {
    return payload.header;
}
function getStage(payload) {
    return payload.stage;
}
function convertCallbacks(callbacks, callbackFactory) {
    const converted = callbacks.map((x) => {
        // This gives preference to the provided factory and falls back to our default implementation
        return (callbackFactory || createCallback)(x) || createCallback(x);
    });
    return converted;
}
function createJourneyStep(payload, callbackFactory) {
    // Redux Toolkit freezes data, so we need to clone it before making any changes
    const unfrozenPayload = typeof structuredClone === 'function'
        ? structuredClone(payload)
        : JSON.parse(JSON.stringify(payload));
    const convertedCallbacks = unfrozenPayload.callbacks
        ? convertCallbacks(unfrozenPayload.callbacks, callbackFactory)
        : [];
    return {
        payload: unfrozenPayload,
        callbacks: convertedCallbacks,
        type: StepType.Step,
        getCallbackOfType: (type) => getCallbackOfType(convertedCallbacks, type),
        getCallbacksOfType: (type) => getCallbacksOfType(convertedCallbacks, type),
        setCallbackValue: (type, value) => setCallbackValue(convertedCallbacks, type, value),
        getDescription: () => getDescription(payload),
        getHeader: () => getHeader(payload),
        getStage: () => getStage(payload),
    };
}
export { createJourneyStep };
